import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, ThumbsUp, ThumbsDown, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function NpsDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/nps/dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 animate-slide-up" data-testid="nps-dashboard">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-emerald-600/15 border border-emerald-500/20 flex items-center justify-center"><Star className="w-6 h-6 text-emerald-400" /></div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Customer Voice</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">NPS Dashboard</h1>
          <p className="text-sm text-slate-400 mt-1">1-click ratings collected after each completed job · Promoter ≥9 · Detractor ≤6</p>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300 mb-2">Overall NPS</div>
          <div className="font-display text-5xl font-semibold text-emerald-300">{data.nps_score ?? "—"}</div>
          <div className="text-xs text-slate-400 mt-1">{data.total} responses</div>
        </div>
        <KpiCard icon={ThumbsUp} label="Promoters" value={data.promoters} sub={`${data.promoter_pct || 0}%`} color="emerald" />
        <KpiCard icon={MessageSquare} label="Passives" value={data.passives} color="slate" />
        <KpiCard icon={ThumbsDown} label="Detractors" value={data.detractors} sub={`${data.detractor_pct || 0}%`} color="red" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Top Body Shops by NPS</div>
          {data.by_body_shop.length === 0 ? <div className="text-center text-slate-500 py-6 text-sm">No data yet</div> :
            <div className="space-y-2">
              {data.by_body_shop.slice(0, 10).map((bs, i) => (
                <div key={i} data-testid={`bs-nps-${i}`} className="flex items-center justify-between rounded-lg bg-slate-900/40 border border-slate-800 px-3 py-2">
                  <div className="flex items-center gap-3"><span className="text-[10px] uppercase font-bold text-slate-500 w-5">#{i+1}</span><span className="text-sm">{bs.name}</span></div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-slate-500">{bs.total} · avg {bs.avg}</span>
                    <Badge className={bs.nps >= 50 ? "bg-emerald-500/15 text-emerald-300" : bs.nps >= 0 ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}>{bs.nps}</Badge>
                  </div>
                </div>
              ))}
            </div>}
        </div>
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Recent Feedback</div>
          {data.recent.length === 0 ? <div className="text-center text-slate-500 py-6 text-sm">Nothing yet</div> :
            <div className="space-y-2">
              {data.recent.map((r, i) => (
                <div key={i} className="rounded-lg bg-slate-900/40 border border-slate-800 p-3">
                  <div className="flex items-center justify-between"><span className="text-xs font-semibold">{r.reviewer_name || r.customer_name}</span>
                    <Badge className={r.category === "promoter" ? "bg-emerald-500/15 text-emerald-300" : r.category === "detractor" ? "bg-red-500/15 text-red-300" : "bg-slate-800 text-slate-400"}>{r.score}/10</Badge>
                  </div>
                  {r.comment && <div className="text-xs text-slate-400 mt-1 italic">"{r.comment}"</div>}
                  <div className="text-[10px] text-slate-600 mt-1">{r.body_shop_name} · {new Date(r.submitted_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>}
        </div>
      </div>
    </div>
  );
}
const KpiCard = ({ icon: I, label, value, sub, color }) => {
  const c = { emerald: "border-emerald-500/30 text-emerald-300", slate: "border-slate-700 text-slate-300", red: "border-red-500/30 text-red-300" }[color];
  return <div className={`rounded-xl border bg-[#0B1120] p-6 ${c}`}>
    <div className="flex items-center justify-between mb-2"><I className="w-5 h-5" /><div className="font-display text-3xl font-semibold">{value}</div></div>
    <div className="text-xs uppercase tracking-wide font-bold">{label}</div>
    {sub && <div className="text-[10px] opacity-70 mt-1">{sub}</div>}
  </div>;
};
